'use client';

import {
  Autocomplete,
  Box,
  FormControlLabel,
  List,
  Radio,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  type RdsPasswordConfig,
  type RestoreAccount,
  RdsPasswordConfigPasswordManagementTypeEnum,
  Provider,
} from '@repo/api-gw-sdk';
import { useEffect, useMemo } from 'react';

import { AuthorizationTooltip } from '@/components/tooltips/authorizationTooltip';
import { FEATURES_SUPPORT_VERSIONS } from '@/configs/featureConfig';
import { useDAL } from '@/contexts/useDAL';
import { useIsAccountFeatureSupported } from '@/utils/useIsAccountFeatureSupported';

import { PasswordTextField } from '../form/passwordTextField';
import { getCloudFieldConfig } from '../form/validation/cloudFieldValidation';
import { Icon } from '../shared/icon';
import { OptionListItem } from '../shared/optionListItem';

const passwordConfig = getCloudFieldConfig(Provider.Aws, 'rdsPassword', {
  required: true,
});

const DEFAULT_KMS_KEY_ID = '__default__';

export interface PasswordManagementState {
  enabled: boolean;
  mode: 'secrets_manager' | 'self_managed';
  password: string;
  secretsManagerKmsKeyId: string | undefined;
}

export const defaultPasswordManagement: PasswordManagementState = {
  enabled: false,
  mode: 'secrets_manager',
  password: '',
  secretsManagerKmsKeyId: undefined,
};

export const PasswordManagement = ({
  passwordManagement = defaultPasswordManagement,
  accountId,
  regionName,
  restoreAccount,
  onChange,
}: {
  passwordManagement?: PasswordManagementState;
  accountId: string | undefined;
  regionName: string | undefined;
  restoreAccount: RestoreAccount | undefined;
  onChange: (passwordManagement: PasswordManagementState) => void;
}) => {
  const dal = useDAL();
  const isAccountFeatureSupported = useIsAccountFeatureSupported();
  const { isLoading, data: encryptionKeys } =
    dal.restore.encryptionKeys.useList(accountId, regionName, false);

  const isSmSupported =
    !!restoreAccount &&
    isAccountFeatureSupported(
      FEATURES_SUPPORT_VERSIONS.RDS_RESTORE_PASSWORD_MANAGEMENT,
      restoreAccount
    );

  const defaultKmsOption = useMemo(
    () => ({
      id: DEFAULT_KMS_KEY_ID,
      arn: '',
      aliasName: 'Default AWS managed key',
    }),
    []
  );

  const kmsOptions = useMemo(
    () => [defaultKmsOption, ...(encryptionKeys?.keys || [])],
    [defaultKmsOption, encryptionKeys?.keys]
  );

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (
      passwordManagement.secretsManagerKmsKeyId &&
      passwordManagement.secretsManagerKmsKeyId !== DEFAULT_KMS_KEY_ID &&
      !encryptionKeys?.keys?.find(
        (key) => key.arn === passwordManagement.secretsManagerKmsKeyId
      )
    ) {
      onChange({ ...passwordManagement, secretsManagerKmsKeyId: undefined });
    }
  }, [
    encryptionKeys?.keys,
    passwordManagement,
    passwordManagement.secretsManagerKmsKeyId,
    isLoading,
    onChange,
  ]);

  // Toggle ON = "set later" (no config), Toggle OFF = "configure now"
  const setLater = !passwordManagement.enabled;
  const isSecretsManagerMode = passwordManagement.mode === 'secrets_manager';
  const isSelfManagedMode = passwordManagement.mode === 'self_managed';

  const setMode = (mode: 'secrets_manager' | 'self_managed') => {
    onChange({ ...passwordManagement, mode });
  };

  const selectedKmsOption =
    passwordManagement.secretsManagerKmsKeyId === DEFAULT_KMS_KEY_ID
      ? defaultKmsOption
      : passwordManagement.secretsManagerKmsKeyId
        ? encryptionKeys?.keys?.find(
            (x) => x.arn === passwordManagement.secretsManagerKmsKeyId
          ) || null
        : null;

  return (
    <Box className='mx-[40px] my-[24px]'>
      <Stack
        className='group/passwordManagement'
        direction='row'
        alignItems='center'
        justifyContent='space-between'
      >
        <Typography>
          {`I'll reset the master password in AWS after restore`}
        </Typography>
        <Stack direction='row' alignItems='center' gap='8px'>
          <Tooltip title='Eon uses a temporary master password to populate the database, which will need to be reset after restore. Turn off to set the password now.'>
            <Icon
              className='opacity-0 group-hover/passwordManagement:opacity-100 transition-opacity'
              iconClass='material-symbols-help-outline-rounded'
            />
          </Tooltip>
          <Switch
            size='small'
            data-testid='passwordManagementToggle'
            checked={setLater}
            onChange={() => {
              const newEnabled = !passwordManagement.enabled;
              onChange({
                ...passwordManagement,
                enabled: newEnabled,
                mode:
                  newEnabled && !isSmSupported
                    ? 'self_managed'
                    : passwordManagement.mode,
              });
            }}
          />
        </Stack>
      </Stack>
      {!setLater && (
        <Stack className='mt-[20px]' gap='24px'>
          <Typography variant='body1'>
            Choose how to set the master password now
          </Typography>
          <List>
            <AuthorizationTooltip isFeatureSupported={isSmSupported}>
              <OptionListItem
                data-testid='passwordModeSecretsManager'
                isChecked={isSecretsManagerMode && isSmSupported}
                onClick={() => isSmSupported && setMode('secrets_manager')}
                sx={{
                  ...(!isSmSupported && {
                    opacity: 0.5,
                    pointerEvents: 'none',
                  }),
                }}
              >
                <Stack className='w-full' gap='8px'>
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                    gap='40px'
                  >
                    <FormControlLabel
                      checked={isSecretsManagerMode}
                      disabled={!isSmSupported}
                      control={<Radio />}
                      label='Auto-manage password in Secrets Manager'
                    />
                    {isSecretsManagerMode && (
                      <Autocomplete
                        sx={{ width: '50%', flexShrink: 0 }}
                        data-testid='secretsManagerKmsKeySelect'
                        size='small'
                        value={selectedKmsOption}
                        renderOption={(props, option, { index }) => {
                          const isDefault = option.id === DEFAULT_KMS_KEY_ID;
                          return (
                            <li
                              {...props}
                              key={option.id}
                              data-testid={`secretsManagerKmsKeyOption-${isDefault ? 'default' : encryptionKeys?.keys?.indexOf(option)}`}
                              style={{
                                ...props.style,
                                ...(index === 1 && {
                                  borderTop:
                                    '1px solid var(--mui-palette-divider)',
                                }),
                              }}
                            >
                              {option?.aliasName}
                            </li>
                          );
                        }}
                        onChange={(event, newValue) => {
                          event.stopPropagation();
                          onChange({
                            ...passwordManagement,
                            secretsManagerKmsKeyId:
                              newValue?.id === DEFAULT_KMS_KEY_ID
                                ? DEFAULT_KMS_KEY_ID
                                : newValue?.arn,
                          });
                        }}
                        getOptionLabel={(option) => option?.aliasName || ''}
                        isOptionEqualToValue={(option, value) =>
                          option?.id === value?.id
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder='Select KMS key for encryption'
                            label=''
                            slotProps={{ inputLabel: { shrink: true } }}
                          />
                        )}
                        options={kmsOptions}
                      />
                    )}
                  </Stack>
                </Stack>
              </OptionListItem>
            </AuthorizationTooltip>
            <OptionListItem
              data-testid='passwordModeSelfManaged'
              isChecked={isSelfManagedMode}
              onClick={() => setMode('self_managed')}
            >
              <Stack className='w-full' gap='8px'>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                  gap='40px'
                >
                  <FormControlLabel
                    checked={isSelfManagedMode}
                    onClick={() => setMode('self_managed')}
                    control={<Radio />}
                    label='Enter password manually'
                  />
                  {isSelfManagedMode && (
                    <PasswordTextField
                      title=''
                      dataTestId='rdsPasswordInput'
                      placeholder={passwordConfig.placeholder}
                      value={passwordManagement.password}
                      onChange={(event) =>
                        onChange({
                          ...passwordManagement,
                          password: event.target.value,
                        })
                      }
                      sx={{ width: '50%', flexShrink: 0 }}
                      validation={passwordConfig.validation}
                    />
                  )}
                </Stack>
              </Stack>
            </OptionListItem>
          </List>
        </Stack>
      )}
    </Box>
  );
};

export const getPasswordManagementPayload = (
  pm?: PasswordManagementState
): {
  passwordConfig?: RdsPasswordConfig | null;
} => {
  if (!pm?.enabled) {
    return {};
  }

  if (pm.mode === 'self_managed') {
    return {
      passwordConfig: {
        passwordManagementType:
          RdsPasswordConfigPasswordManagementTypeEnum.SelfManaged,
        password: pm.password,
      },
    };
  }

  const hasCustomKmsKey =
    pm.secretsManagerKmsKeyId &&
    pm.secretsManagerKmsKeyId !== DEFAULT_KMS_KEY_ID;

  return {
    passwordConfig: {
      passwordManagementType:
        RdsPasswordConfigPasswordManagementTypeEnum.AwsSecretsManager,
      ...(hasCustomKmsKey
        ? { secretsManagerKmsKeyId: pm.secretsManagerKmsKeyId }
        : {}),
    },
  };
};
